import React from "react";

import menuMidiImg from "@assets/images/scan-to/offers/menu_midi.jpg";
import happyHourImg from "@assets/images/scan-to/offers/happy_hour.jpg";
import dessertImg from "@assets/images/scan-to/offers/dessert.jpg";

import { FALLBACK_OFFERS, IMAGE_PLACEHOLDER_PATH } from "./types";
import { PreviewT } from "./previewI18n";
import {
    PreviewSection,
    PreviewSectionTitle,
    OffersContainer,
    OfferCard,
    OfferCardImage,
    OfferCardBody,
    OfferCardTitle,
    OfferCardDescription,
    OfferDiscount,
} from "./styled";

// Map FALLBACK_OFFERS.imageKey to the actual imported image asset. Kept
// here (not in types.ts) so the constants module stays free of React /
// asset imports.
const FALLBACK_OFFERS_IMAGES: Record<string, string> = {
    menuMidi: menuMidiImg,
    happyHour: happyHourImg,
    dessert: dessertImg,
};

type OffersSectionProps = {
    t: PreviewT;
    offersEnabled: boolean;
    offersTitleVisible: boolean;
    offersConfigured: boolean;
    offersSectionTitle: string;
    offersIconPath: string;
    offersCustomIcon: string;
    offers: Array<{
        id: number;
        title: string;
        description: string;
        tag: string;
        imageUrl?: string;
    }>;
    mainColor: string;
    primaryColor: string;
    secondaryColor: string;
};

export function OffersSection({
    t,
    offersEnabled,
    offersTitleVisible,
    offersConfigured,
    offersSectionTitle,
    offersIconPath,
    offersCustomIcon,
    offers,
    mainColor,
    primaryColor,
    secondaryColor,
}: OffersSectionProps): JSX.Element | null {
    if (!offersEnabled) return null;
    // Once the user has touched the Offres tab (offersConfigured) and
    // deactivated every offer switch, hide the entire section — title,
    // icon, and container — so the empty state stays empty instead of
    // just showing a lonely header above a blank row.
    if (offersConfigured && offers.length === 0) return null;

    return (
        <PreviewSection>
            {offersTitleVisible && (
                <PreviewSectionTitle mainColor={primaryColor}>
                    {offersCustomIcon ? (
                        <img
                            src={offersCustomIcon}
                            alt=""
                            style={{
                                width: 14,
                                height: 14,
                                objectFit: "contain",
                            }}
                        />
                    ) : (
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill={primaryColor}
                        >
                            <path d={offersIconPath} />
                        </svg>
                    )}
                    {offersSectionTitle}
                </PreviewSectionTitle>
            )}
            <OffersContainer>
                {/* Two states reach this point — `offersConfigured &&
                    empty` is handled by an early-return above:
                       • real offers > 0 → render them
                       • tab never visited → render the demo fallback */}
                {offers.length > 0 ? (
                    offers.map((offer) => (
                        <OfferCard mainColor={mainColor} key={offer.id}>
                            <OfferCardImage>
                                {offer.imageUrl ? (
                                    <img src={offer.imageUrl} alt="" />
                                ) : (
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="#ccc"
                                        style={{
                                            margin: "auto",
                                            display: "block",
                                            marginTop: "28px",
                                        }}
                                    >
                                        <path d={IMAGE_PLACEHOLDER_PATH} />
                                    </svg>
                                )}
                            </OfferCardImage>
                            <OfferCardBody>
                                {offer.title && (
                                    <OfferCardTitle>
                                        {offer.title}
                                    </OfferCardTitle>
                                )}
                                {offer.description && (
                                    <OfferCardDescription>
                                        {offer.description}
                                    </OfferCardDescription>
                                )}
                                {offer.tag && (
                                    <OfferDiscount
                                        mainColor={mainColor}
                                        secondaryColor={secondaryColor}
                                    >
                                        {offer.tag}
                                    </OfferDiscount>
                                )}
                            </OfferCardBody>
                        </OfferCard>
                    ))
                ) : (
                    <>
                        {FALLBACK_OFFERS.map((fallback) => (
                            <OfferCard mainColor={mainColor} key={fallback.id}>
                                <OfferCardImage>
                                    <img
                                        src={
                                            FALLBACK_OFFERS_IMAGES[
                                                fallback.imageKey
                                            ]
                                        }
                                        alt=""
                                    />
                                </OfferCardImage>
                                <OfferCardBody>
                                    <OfferCardTitle>
                                        {t(fallback.titleKey)}
                                    </OfferCardTitle>
                                    <OfferCardDescription>
                                        {t(fallback.descriptionKey)}
                                    </OfferCardDescription>
                                    <OfferDiscount
                                        mainColor={mainColor}
                                        secondaryColor={secondaryColor}
                                    >
                                        {fallback.tag}
                                    </OfferDiscount>
                                </OfferCardBody>
                            </OfferCard>
                        ))}
                    </>
                )}
            </OffersContainer>
        </PreviewSection>
    );
}
